import { logger } from '../services/logger';

interface RetryOptions {
  maxAttempts?: number;
  backoffMs?: number;
  retryableErrors?: string[];
  onRetry?: (attempt: number, error: any) => void;
}

export async function withRetry<T>(
  operation: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    maxAttempts = 3,
    backoffMs = 1000,
    retryableErrors = [
      'Failed to fetch',
      'NetworkError',
      'network request failed',
      'Network request failed',
      'timeout',
      'socket hang up',
      'ECONNRESET'
    ],
    onRetry = (attempt: number, error: any) => {
      logger.warn(`Retry attempt ${attempt}`, { error });
    }
  } = options;

  let lastError: any;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await operation();
    } catch (error: any) {
      lastError = error;

      // Don't retry for auth or data errors
      if (error.code?.startsWith('PGRST') || 
          error.code?.startsWith('23') ||
          error.code === '42501') { // RLS policy violation
        throw error;
      }

      // Check if error is retryable
      const shouldRetry = retryableErrors.some(errStr => 
        error.message?.toLowerCase().includes(errStr.toLowerCase())
      );

      if (!shouldRetry || attempt === maxAttempts) {
        throw error;
      }

      // Calculate backoff delay
      const delay = backoffMs * Math.pow(2, attempt - 1);
      
      // Log retry attempt
      onRetry(attempt, error);

      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  throw lastError;
}